import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import React, {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from 'react'
import { CircleHelp, X } from 'lucide-react'
import { useAuth } from '../auth/AuthContext'
import { api } from '../lib/api'
import { useToast } from '../ui/ToastProvider'
import OrgMultiSelect from './OrgMultiSelect'

const PERSONAL_CATEGORY_OPTIONS = [
    { value: '', label: '선택 안 함' },
    { value: 'MEETING', label: '회의' },
    { value: 'CLIENT_MEETING', label: '미팅' },
    { value: 'TRIP', label: '출장' },
    { value: 'LEAVE', label: '휴가' },
    { value: 'TRAINING', label: '교육' },
    { value: 'OTHER', label: '기타' },
]

const RRULE_PRESETS = [
    { key: 'none', value: '', label: '반복 없음' },
    { key: 'daily', value: 'FREQ=DAILY;INTERVAL=1', label: '매일' },
    { key: 'weekdays', value: 'FREQ=WEEKLY;BYDAY=MO,TU,WE,TH,FR', label: '매주 평일' },
    { key: 'weekly', value: 'FREQ=WEEKLY;INTERVAL=1', label: '매주 (같은 요일)' },
]

function unwrap(res) {
    const d = res?.data
    if (d && d.data !== undefined) return d.data
    return d
}

function toLocalInput(d) {
    const pad = (n) => String(n).padStart(2, '0')
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

const ONE_HOUR_MS = 60 * 60 * 1000

function isLocalMidnight(d) {
    return d instanceof Date
        && Number.isFinite(d.getTime())
        && d.getHours() === 0
        && d.getMinutes() === 0
        && d.getSeconds() === 0
        && d.getMilliseconds() === 0
}

function isNextDayLocalMidnight(start, end) {
    if (!isLocalMidnight(start) || !isLocalMidnight(end)) return false
    const next = new Date(start.getFullYear(), start.getMonth(), start.getDate() + 1)
    return end.getTime() === next.getTime()
}

function startOfLocalDay(d) {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate())
}

function endOfLocalDay(d) {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999)
}

function normalizeAllDayRangeFromDates(startDate, endDate) {
    if (!(startDate instanceof Date) || !Number.isFinite(startDate.getTime())) return null
    const start = startOfLocalDay(startDate)
    let endSource = endDate instanceof Date && Number.isFinite(endDate.getTime()) ? endDate : startDate
    if (isLocalMidnight(endSource) && endSource > start) {
        endSource = new Date(endSource.getTime() - 1)
    }
    let end = endOfLocalDay(endSource)
    if (!(end > start)) end = endOfLocalDay(start)
    return { start, end }
}

function parseIso(s) {
    if (!s) return null
    const d = new Date(s)
    return Number.isNaN(d.getTime()) ? null : d
}

function validateEventStartEndOrder(startLocal, endLocal) {
    if (!startLocal || !endLocal) {
        return { ok: false, message: '시작·종료 시각을 입력해 주세요.' }
    }
    const s = new Date(startLocal)
    const e = new Date(endLocal)
    if (!Number.isFinite(s.getTime()) || !Number.isFinite(e.getTime())) {
        return { ok: false, message: '시작·종료 시각이 올바르지 않습니다.' }
    }
    if (!(e > s)) {
        return { ok: false, message: '시작은 종료보다 이전이어야 하고, 종료는 시작보다 이후여야 합니다.' }
    }
    return { ok: true }
}

const CUSTOMER_LINK_LABELS = {
    field: '\uC5F0\uACB0 \uACE0\uAC1D (\uC120\uD0DD)',
    placeholder: '\uACE0\uAC1D \uC774\uB984 \uB610\uB294 \uC5F0\uB77D\uCC98 \uAC80\uC0C9',
    linked: '\uC5F0\uACB0 \uACE0\uAC1D',
    unlink: '\uACE0\uAC1D \uC5F0\uACB0 \uD574\uC81C',
    detected: '\uC81C\uBAA9\uC5D0\uC11C \uACE0\uAC1D \uC774\uB984\uC744 \uCC3E\uC558\uC2B5\uB2C8\uB2E4.',
    choose: '\uB3D9\uC77C\uD55C \uC774\uB984\uC758 \uACE0\uAC1D\uC774 \uC788\uC2B5\uB2C8\uB2E4. \uC5F0\uACB0\uD560 \uACE0\uAC1D\uC744 \uC120\uD0DD\uD574 \uC8FC\uC138\uC694.',
    confirmSuffix: ' \uACE0\uAC1D\uACFC\uC758 \uC77C\uC815\uC778\uAC00\uC694?',
    skip: '\uC5F0\uACB0\uD558\uC9C0 \uC54A\uC74C',
    searching: '\uACE0\uAC1D \uAC80\uC0C9 \uC911...',
    empty: '\uAC80\uC0C9 \uACB0\uACFC\uAC00 \uC5C6\uC2B5\uB2C8\uB2E4.',
}

function customerCandidateLabel(candidate) {
    const name = candidate?.name || `Customer #${candidate?.id || ''}`
    return candidate?.phoneMasked ? `${name} (${candidate.phoneMasked})` : name
}

/**
 * 일정 추가/수정 모달(일정관리·대시보드 공용). 부모는 ref로 open* 호출.
 */
const CalendarEventEditor = forwardRef(function CalendarEventEditor(_props, ref) {
    const { user } = useAuth()
    const { show } = useToast()
    const qc = useQueryClient()
    const currentUserId = user?.id != null ? Number(user.id) : null

    const [modal, setModal] = useState(null)
    const [editDetail, setEditDetail] = useState(null)
    const [title, setTitle] = useState('')
    const [description, setDescription] = useState('')
    const [startLocal, setStartLocal] = useState('')
    const [endLocal, setEndLocal] = useState('')
    const [allDay, setAllDay] = useState(false)
    const [remindersEnabled, setRemindersEnabled] = useState(true)
    const [rem10, setRem10] = useState(false)
    const [rem60, setRem60] = useState(true)
    const [rem1440, setRem1440] = useState(false)
    const [orgTargetConfig, setOrgTargetConfig] = useState([])
    const [orgPickerIds, setOrgPickerIds] = useState([])
    const [explicitUserIds, setExplicitUserIds] = useState([])
    const [candidates, setCandidates] = useState([])
    const [rrule, setRrule] = useState('')
    const [personalCategory, setPersonalCategory] = useState('')
    const [busyShareOrgId, setBusyShareOrgId] = useState(null)
    const [busyShareHelpOpen, setBusyShareHelpOpen] = useState(false)
    const [linkedCustomer, setLinkedCustomer] = useState(null)
    const [titleCustomerCandidates, setTitleCustomerCandidates] = useState([])
    const [dismissedCustomerTitle, setDismissedCustomerTitle] = useState('')
    const [customerSearchQuery, setCustomerSearchQuery] = useState('')
    const [customerSearchResults, setCustomerSearchResults] = useState([])
    const [customerSearchLoading, setCustomerSearchLoading] = useState(false)

    const syncOrgTargetsFromPicker = useCallback((ids) => {
        setOrgPickerIds(ids)
        setOrgTargetConfig((prev) => {
            const next = ids.map((id) => {
                const n = Number(id)
                const found = prev.find((x) => x.orgId === n)
                return found || { orgId: n, includeDescendants: true }
            })
            return next
        })
    }, [])

    const orgOptionsQuery = useQuery({
        queryKey: ['orgs-options', 'mine', currentUserId],
        queryFn: async () => {
            const res = await api.get('/orgs/options', { params: { scope: 'mine' } })
            const data = unwrap(res)
            return Array.isArray(data) ? data : []
        },
        enabled: Boolean(currentUserId),
    })

    const orgNameById = useMemo(() => {
        const m = new Map()
        for (const o of orgOptionsQuery.data || []) {
            const id = Number(o.id)
            if (id) m.set(id, o.name || o.orgName || `조직 ${id}`)
        }
        return m
    }, [orgOptionsQuery.data])

    useEffect(() => {
        if ((modal !== 'org' && modal !== 'edit-org') || !orgTargetConfig.length) {
            setCandidates([])
            return
        }
        let cancelled = false
        ;(async () => {
            try {
                const body = {
                    orgTargets: orgTargetConfig.map((r) => ({
                        orgId: r.orgId,
                        includeDescendants: !!r.includeDescendants,
                    })),
                }
                const res = await api.post('/calendar/candidate-users', body)
                const data = unwrap(res)
                if (!cancelled) setCandidates(Array.isArray(data) ? data : [])
            } catch {
                if (!cancelled) setCandidates([])
            }
        })()
        return () => { cancelled = true }
    }, [modal, orgTargetConfig])

    const isPersonalMode = modal === 'personal' || modal === 'edit-personal'

    useEffect(() => {
        const normalizedTitle = title.trim()
        if (!isPersonalMode || linkedCustomer || normalizedTitle.length < 2 || dismissedCustomerTitle === normalizedTitle) {
            setTitleCustomerCandidates([])
            return
        }
        let cancelled = false
        const timer = setTimeout(async () => {
            try {
                const res = await api.get('/calendar/customer-link-candidates', {
                    params: { q: normalizedTitle, source: 'title', limit: 8 },
                    __silent: true,
                })
                const data = unwrap(res)
                if (!cancelled) setTitleCustomerCandidates(Array.isArray(data) ? data : [])
            } catch {
                if (!cancelled) setTitleCustomerCandidates([])
            }
        }, 450)
        return () => {
            cancelled = true
            clearTimeout(timer)
        }
    }, [dismissedCustomerTitle, isPersonalMode, linkedCustomer, title])

    useEffect(() => {
        const query = customerSearchQuery.trim()
        if (!isPersonalMode || linkedCustomer || query.length < 2) {
            setCustomerSearchResults([])
            setCustomerSearchLoading(false)
            return
        }
        let cancelled = false
        const timer = setTimeout(async () => {
            setCustomerSearchLoading(true)
            try {
                const res = await api.get('/calendar/customer-link-candidates', {
                    params: { q: query, source: 'search', limit: 8 },
                    __silent: true,
                })
                const data = unwrap(res)
                if (!cancelled) setCustomerSearchResults(Array.isArray(data) ? data : [])
            } catch {
                if (!cancelled) setCustomerSearchResults([])
            } finally {
                if (!cancelled) setCustomerSearchLoading(false)
            }
        }, 350)
        return () => {
            cancelled = true
            clearTimeout(timer)
        }
    }, [customerSearchQuery, isPersonalMode, linkedCustomer])

    const selectLinkedCustomer = useCallback((candidate) => {
        if (!candidate?.id) return
        setLinkedCustomer(candidate)
        setTitleCustomerCandidates([])
        setCustomerSearchQuery('')
        setCustomerSearchResults([])
        setDismissedCustomerTitle('')
    }, [])

    const unresolvedCustomerSuggestion = isPersonalMode
        && !linkedCustomer
        && titleCustomerCandidates.length > 0
        && dismissedCustomerTitle !== title.trim()

    const reminderOffsetsMinutes = useMemo(() => {
        if (!remindersEnabled) return []
        const o = []
        if (rem10) o.push(10)
        if (rem60) o.push(60)
        if (rem1440) o.push(1440)
        return o
    }, [remindersEnabled, rem10, rem60, rem1440])

    const startEndOrder = useMemo(
        () => validateEventStartEndOrder(startLocal, endLocal),
        [startLocal, endLocal],
    )

    function currentIsoRange() {
        const s = new Date(startLocal)
        const e = new Date(endLocal)
        if (allDay) {
            const range = normalizeAllDayRangeFromDates(s, e)
            if (range) return { startAt: range.start.toISOString(), endAt: range.end.toISOString() }
        }
        return { startAt: s.toISOString(), endAt: e.toISOString() }
    }

    function updateAllDay(checked) {
        setAllDay(checked)
        if (!checked) {
            const now = new Date()
            const selectedDate = new Date(startLocal)
            const baseDate = Number.isFinite(selectedDate.getTime()) ? selectedDate : now
            const isToday = baseDate.getFullYear() === now.getFullYear()
                && baseDate.getMonth() === now.getMonth()
                && baseDate.getDate() === now.getDate()
            const start = new Date(baseDate.getFullYear(), baseDate.getMonth(), baseDate.getDate())
            start.setHours(isToday ? now.getHours() : 9, isToday ? now.getMinutes() : 0, 0, 0)
            const end = new Date(start.getTime() + ONE_HOUR_MS)
            setStartLocal(toLocalInput(start))
            setEndLocal(toLocalInput(end))
            return
        }
        setRrule('')
        const s = new Date(startLocal)
        const e = new Date(endLocal)
        const range = normalizeAllDayRangeFromDates(s, e)
        if (range) {
            setStartLocal(toLocalInput(range.start))
            setEndLocal(toLocalInput(range.end))
        }
    }

    function updateReminderChecked(key, checked) {
        const next10 = key === 10 ? checked : rem10
        const next60 = key === 60 ? checked : rem60
        const next1440 = key === 1440 ? checked : rem1440
        setRem10(next10)
        setRem60(next60)
        setRem1440(next1440)
        setRemindersEnabled(next10 || next60 || next1440)
    }

    function clearReminders() {
        setRemindersEnabled(false)
        setRem10(false)
        setRem60(false)
        setRem1440(false)
    }

    function resetPersonalCreateDefaults() {
        setTitle('')
        setDescription('')
        setRrule('')
        setPersonalCategory('')
        setBusyShareOrgId(null)
        setBusyShareHelpOpen(false)
        setLinkedCustomer(null)
        setTitleCustomerCandidates([])
        setDismissedCustomerTitle('')
        setCustomerSearchQuery('')
        setCustomerSearchResults([])
        setCustomerSearchLoading(false)
        setAllDay(false)
        setRemindersEnabled(true)
        setRem10(false)
        setRem60(true)
        setRem1440(false)
    }

    function openPersonalAt(startDate, endDate) {
        setModal('personal')
        setEditDetail(null)
        resetPersonalCreateDefaults()
        const rawStart = startDate instanceof Date ? new Date(startDate.getTime()) : null
        const rawEnd = endDate instanceof Date ? new Date(endDate.getTime()) : null
        let d = rawStart ? new Date(rawStart.getTime()) : new Date()
        if (Number.isNaN(d.getTime())) {
            d = new Date()
            d.setMinutes(0, 0, 0)
        } else if (d.getHours() === 0 && d.getMinutes() === 0 && d.getSeconds() === 0 && d.getMilliseconds() === 0) {
            d.setHours(9, 0, 0, 0)
        } else {
            d.setMinutes(0, 0, 0)
        }
        let e = rawEnd ? new Date(rawEnd.getTime()) : null
        const dateOnlySingleSlot = rawStart && rawEnd && isNextDayLocalMidnight(rawStart, rawEnd)
        if (!e || Number.isNaN(e.getTime()) || !(e > d) || dateOnlySingleSlot) {
            e = new Date(d.getTime() + ONE_HOUR_MS)
        }
        setStartLocal(toLocalInput(d))
        setEndLocal(toLocalInput(e))
    }

    function openOrg() {
        setModal('org')
        setEditDetail(null)
        setTitle('')
        setDescription('')
        setRrule('')
        setPersonalCategory('')
        setBusyShareOrgId(null)
        setBusyShareHelpOpen(false)
        setLinkedCustomer(null)
        setTitleCustomerCandidates([])
        setDismissedCustomerTitle('')
        setCustomerSearchQuery('')
        setCustomerSearchResults([])
        setCustomerSearchLoading(false)
        setOrgPickerIds([])
        setOrgTargetConfig([])
        setExplicitUserIds([])
        const d = new Date()
        d.setMinutes(0, 0, 0)
        const e = new Date(d.getTime() + 60 * 60 * 1000)
        setStartLocal(toLocalInput(d))
        setEndLocal(toLocalInput(e))
        setAllDay(false)
        setRemindersEnabled(true)
        setRem10(false)
        setRem60(true)
        setRem1440(false)
    }

    function closeModal() {
        setModal(null)
        setEditDetail(null)
        setBusyShareHelpOpen(false)
        setTitleCustomerCandidates([])
        setCustomerSearchQuery('')
        setCustomerSearchResults([])
        setCustomerSearchLoading(false)
    }

    function prefillFromDetail(d) {
        setTitle(d.title || '')
        setDescription(d.description || '')
        const s = parseIso(d.startAt)
        const e = parseIso(d.endAt)
        const detailAllDay = !!d.allDay
        if (detailAllDay && s) {
            const range = normalizeAllDayRangeFromDates(s, e)
            if (range) {
                setStartLocal(toLocalInput(range.start))
                setEndLocal(toLocalInput(range.end))
            }
        } else {
            if (s) setStartLocal(toLocalInput(s))
            if (e) setEndLocal(toLocalInput(e))
        }
        setAllDay(detailAllDay)
        let arr = []
        try {
            if (d.reminderOffsetsJson) arr = JSON.parse(d.reminderOffsetsJson)
        } catch { arr = [] }
        if (!Array.isArray(arr)) arr = []
        setRemindersEnabled(d.useDefaultReminders !== false || arr.length > 0)
        setRem10(arr.includes(10))
        setRem60(arr.includes(60) || (d.useDefaultReminders !== false && !arr.length))
        setRem1440(arr.includes(1440))
        if (d.eventKind === 'ORG' && Array.isArray(d.orgTargets)) {
            const cfg = d.orgTargets.map((t) => ({
                orgId: Number(t.orgId),
                includeDescendants: !!t.includeDescendants,
            }))
            setOrgTargetConfig(cfg)
            setOrgPickerIds(cfg.map((x) => x.orgId))
        } else {
            setOrgTargetConfig([])
            setOrgPickerIds([])
        }
        setExplicitUserIds(Array.isArray(d.explicitUserIds) ? d.explicitUserIds.map(Number) : [])
        setRrule(typeof d.rrule === 'string' ? d.rrule : '')
        setPersonalCategory(d.personalCategory != null && String(d.personalCategory).trim() ? String(d.personalCategory).trim() : '')
        setBusyShareOrgId(d.busyShareOrgId != null ? Number(d.busyShareOrgId) : null)
        setBusyShareHelpOpen(false)
        const customerRefId = Number(d.customerRefId)
        setLinkedCustomer(Number.isFinite(customerRefId) && customerRefId > 0 ? {
            id: customerRefId,
            name: d.customerName || `Customer #${customerRefId}`,
            phoneMasked: d.customerPhoneMasked || '',
        } : null)
        setTitleCustomerCandidates([])
        setDismissedCustomerTitle('')
        setCustomerSearchQuery('')
        setCustomerSearchResults([])
        setCustomerSearchLoading(false)
    }
    function startEditFromDetail(d) {
        if (!d) return
        prefillFromDetail(d)
        setEditDetail(d)
        setModal(d.eventKind === 'ORG' ? 'edit-org' : 'edit-personal')
    }

    const createPersonalMut = useMutation({
        mutationFn: async () => {
            const vr = validateEventStartEndOrder(startLocal, endLocal)
            if (!vr.ok) throw new Error(vr.message)
            const { startAt, endAt } = currentIsoRange()
            const body = {
                title: title.trim(),
                description: description.trim() || null,
                startAt,
                endAt,
                allDay,
                timeZone: 'Asia/Seoul',
                useDefaultReminders: false,
                reminderOffsetsMinutes,
                rrule: allDay ? null : (rrule.trim() || null),
                customerRefId: linkedCustomer?.id || null,
                personalCategory: personalCategory.trim() || null,
                hideDetailsForOrgAdmins: false,
                busyShareOrgId: busyShareOrgId != null && busyShareOrgId > 0 ? busyShareOrgId : null,
            }
            const res = await api.post('/calendar/events/personal', body)
            return unwrap(res)
        },
        onSuccess: () => {
            show('개인 일정이 등록되었습니다', 'success')
            qc.invalidateQueries({ queryKey: ['calendar-events'] })
            qc.invalidateQueries({ queryKey: ['calendar-team-events'] })
            closeModal()
        },
        onError: (e) => {
            const msg = e?.response?.data?.message || e?.message || '등록에 실패했습니다'
            show(msg, 'error')
        },
    })

    const createOrgMut = useMutation({
        mutationFn: async () => {
            if (!orgTargetConfig.length) throw new Error('대상 조직을 선택하세요')
            const vr = validateEventStartEndOrder(startLocal, endLocal)
            if (!vr.ok) throw new Error(vr.message)
            const { startAt, endAt } = currentIsoRange()
            const body = {
                hostOrgId: user?.orgId ?? null,
                title: title.trim(),
                description: description.trim() || null,
                startAt,
                endAt,
                allDay,
                timeZone: 'Asia/Seoul',
                useDefaultReminders: false,
                reminderOffsetsMinutes,
                orgTargets: orgTargetConfig.map((r) => ({
                    orgId: r.orgId,
                    includeDescendants: !!r.includeDescendants,
                })),
                explicitUserIds: explicitUserIds.length ? explicitUserIds : null,
                rrule: allDay ? null : (rrule.trim() || null),
            }
            const res = await api.post('/calendar/events/org', body)
            return unwrap(res)
        },
        onSuccess: () => {
            show('조직 일정이 등록되었습니다', 'success')
            qc.invalidateQueries({ queryKey: ['calendar-events'] })
            qc.invalidateQueries({ queryKey: ['calendar-team-events'] })
            closeModal()
        },
        onError: (e) => {
            const msg = e?.response?.data?.message || e?.message || '등록에 실패했습니다'
            show(msg, 'error')
        },
    })

    const updateMut = useMutation({
        mutationFn: async ({ id, payload }) => {
            const res = await api.patch(`/calendar/events/${id}`, payload)
            return unwrap(res)
        },
        onSuccess: (_data, variables) => {
            show('일정을 수정했습니다', 'success')
            qc.invalidateQueries({ queryKey: ['calendar-events'] })
            qc.invalidateQueries({ queryKey: ['calendar-team-events'] })
            qc.invalidateQueries({ queryKey: ['calendar-event'] })
            closeModal()
        },
        onError: (e) => {
            const msg = e?.response?.data?.message || e?.message || '수정에 실패했습니다'
            show(msg, 'error')
        },
    })

    function submitEdit() {
        const d = editDetail
        if (!d) return
        const vr = validateEventStartEndOrder(startLocal, endLocal)
        if (!vr.ok) {
            show(vr.message, 'error')
            return
        }
        const { startAt, endAt } = currentIsoRange()
        const base = {
            title: title.trim(),
            description: description.trim() || null,
            startAt,
            endAt,
            allDay,
            timeZone: d.timeZone || 'Asia/Seoul',
            useDefaultReminders: false,
            reminderOffsetsMinutes,
            rrule: allDay ? '' : rrule.trim(),
        }
        if (d.eventKind === 'ORG') {
            updateMut.mutate({
                id: d.id,
                payload: {
                    ...base,
                    orgTargets: orgTargetConfig.map((r) => ({
                        orgId: r.orgId,
                        includeDescendants: !!r.includeDescendants,
                    })),
                    explicitUserIds: [...explicitUserIds],
                },
            })
        } else {
            const hadBusy = d.busyShareOrgId != null && Number(d.busyShareOrgId) > 0
            const clearingBusy = hadBusy && (busyShareOrgId == null || busyShareOrgId <= 0)
            const originalCustomerId = Number(d.customerRefId)
            const nextCustomerId = Number(linkedCustomer?.id)
            const customerPatch = {}
            if (Number.isFinite(nextCustomerId) && nextCustomerId > 0
                && nextCustomerId !== originalCustomerId) {
                customerPatch.customerRefId = nextCustomerId
            } else if (Number.isFinite(originalCustomerId) && originalCustomerId > 0
                && !(Number.isFinite(nextCustomerId) && nextCustomerId > 0)) {
                customerPatch.clearCustomerRef = true
            }
            updateMut.mutate({
                id: d.id,
                payload: {
                    ...base,
                    orgTargets: null,
                    explicitUserIds: null,
                    personalCategory: personalCategory.trim() || '',
                    hideDetailsForOrgAdmins: false,
                    busyShareOrgId: busyShareOrgId != null && busyShareOrgId > 0 ? busyShareOrgId : null,
                    ...(clearingBusy ? { clearBusyShareOrg: true } : {}),
                    ...customerPatch,
                },
            })
        }
    }

    const apiRef = useRef({
        openPersonalAt,
        openOrg,
        startEditFromDetail,
        closeModal,
    })
    apiRef.current = { openPersonalAt, openOrg, startEditFromDetail, closeModal }

    useImperativeHandle(ref, () => ({
        openPersonalAt: (start, end) => apiRef.current.openPersonalAt(start, end),
        openOrg: () => apiRef.current.openOrg(),
        startEditFromDetail: (d) => apiRef.current.startEditFromDetail(d),
        /** 목록 요약만 있을 때 상세 조회 후 수정 모달 */
        openEditFromEventSummary: async (ev) => {
            if (!ev?.id) return
            try {
                const res = await api.get(`/calendar/events/${ev.id}`)
                const d = unwrap(res)
                apiRef.current.startEditFromDetail(d)
            } catch (e) {
                const msg = e?.response?.data?.message || e?.message || '일정을 불러오지 못했습니다'
                show(msg, 'error')
            }
        },
        closeModal: () => apiRef.current.closeModal(),
    }), [show])

    if (!modal) return null

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog">
            <div className="bg-white rounded-lg shadow-lg max-w-xl w-full p-4 space-y-3 max-h-[90vh] overflow-y-auto">
                <div className="font-semibold">
                    {modal === 'org' && '조직 일정'}
                    {modal === 'personal' && '개인 일정'}
                    {modal === 'edit-org' && '조직 일정 수정'}
                    {modal === 'edit-personal' && '개인 일정 수정'}
                </div>
                <label className="block text-sm">
                    <span className="text-slate-600">제목</span>
                    <input className="mt-1 w-full rounded border px-3 py-2" value={title} onChange={(e) => setTitle(e.target.value)} />
                </label>
                <div className="grid gap-3">
                    <div className="grid gap-2 sm:grid-cols-2">
                        <label className="flex min-h-10 items-center gap-2 rounded border border-slate-200 px-3 py-2 text-sm">
                            <input
                                type="checkbox"
                                checked={allDay}
                                onChange={(e) => updateAllDay(e.target.checked)}
                            />
                            <span>{'\uD558\uB8E8\uC885\uC77C'}</span>
                        </label>
                {isPersonalMode && (
                    <div className="order-first space-y-2 sm:col-span-2">
                        {linkedCustomer ? (
                            <div className="flex min-h-10 items-center justify-between gap-2 rounded border border-sky-200 bg-sky-50 px-3 py-2 text-sm">
                                <div className="min-w-0">
                                    <span className="mr-2 text-xs font-semibold text-sky-700">{CUSTOMER_LINK_LABELS.linked}</span>
                                    <span className="font-medium text-slate-900">{customerCandidateLabel(linkedCustomer)}</span>
                                </div>
                                <button
                                    type="button"
                                    className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded text-slate-500 hover:bg-white hover:text-slate-900"
                                    aria-label={CUSTOMER_LINK_LABELS.unlink}
                                    title={CUSTOMER_LINK_LABELS.unlink}
                                    onClick={() => {
                                        setLinkedCustomer(null)
                                        setDismissedCustomerTitle(title.trim())
                                    }}
                                >
                                    <X className="h-4 w-4" aria-hidden="true" />
                                </button>
                            </div>
                        ) : (
                            <>
                                {titleCustomerCandidates.length > 0
                                    && dismissedCustomerTitle !== title.trim() ? (
                                    <div className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-sm" role="status">
                                        <p className="font-medium text-amber-950">
                                            {titleCustomerCandidates.length === 1
                                                ? `${customerCandidateLabel(titleCustomerCandidates[0])}${CUSTOMER_LINK_LABELS.confirmSuffix}`
                                                : CUSTOMER_LINK_LABELS.choose}
                                        </p>
                                        <div className="mt-2 flex flex-wrap gap-2">
                                            {titleCustomerCandidates.map((candidate) => (
                                                <button
                                                    key={candidate.id}
                                                    type="button"
                                                    className="rounded border border-amber-400 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-800 hover:bg-amber-100"
                                                    onClick={() => selectLinkedCustomer(candidate)}
                                                >
                                                    {customerCandidateLabel(candidate)}
                                                </button>
                                            ))}
                                            <button
                                                type="button"
                                                className="rounded px-2.5 py-1.5 text-xs font-medium text-amber-900 hover:bg-amber-100"
                                                onClick={() => {
                                                    setDismissedCustomerTitle(title.trim())
                                                    setTitleCustomerCandidates([])
                                                }}
                                            >
                                                {CUSTOMER_LINK_LABELS.skip}
                                            </button>
                                        </div>
                                    </div>
                                ) : null}
                                <label className="block text-sm">
                                    <span className="text-slate-600">{CUSTOMER_LINK_LABELS.field}</span>
                                    <input
                                        className="mt-1 w-full rounded border px-3 py-2"
                                        value={customerSearchQuery}
                                        onChange={(e) => setCustomerSearchQuery(e.target.value)}
                                        placeholder={CUSTOMER_LINK_LABELS.placeholder}
                                        autoComplete="off"
                                    />
                                </label>
                                {customerSearchQuery.trim().length >= 2 ? (
                                    <div className="max-h-36 overflow-y-auto rounded border border-slate-200 bg-white p-1">
                                        {customerSearchLoading ? (
                                            <p className="px-2 py-2 text-xs text-slate-500">{CUSTOMER_LINK_LABELS.searching}</p>
                                        ) : customerSearchResults.length ? customerSearchResults.map((candidate) => (
                                            <button
                                                key={candidate.id}
                                                type="button"
                                                className="block w-full rounded px-2 py-2 text-left text-sm hover:bg-slate-100"
                                                onClick={() => selectLinkedCustomer(candidate)}
                                            >
                                                {customerCandidateLabel(candidate)}
                                            </button>
                                        )) : (
                                            <p className="px-2 py-2 text-xs text-slate-500">{CUSTOMER_LINK_LABELS.empty}</p>
                                        )}
                                    </div>
                                ) : null}
                            </>
                        )}
                    </div>
                )}
                        <label className="hidden" aria-hidden="true">
                            <input
                                type="checkbox"
                                checked={allDay}
                                onChange={(e) => updateAllDay(e.target.checked)}
                            />
                            <span>하루종일</span>
                        </label>
                        {(modal === 'personal' || modal === 'edit-personal') && user?.orgId && String(user?.orgType || '').toUpperCase() !== 'PERSONAL' ? (
                            <div className="relative">
                                <label className="flex min-h-10 items-center gap-2 rounded border border-slate-200 px-3 py-2 text-sm">
                                    <input
                                        type="checkbox"
                                        checked={Number(busyShareOrgId) === Number(user.orgId)}
                                        onChange={(e) => {
                                            if (e.target.checked) setBusyShareOrgId(Number(user.orgId))
                                            else setBusyShareOrgId(null)
                                        }}
                                    />
                                    <span className="min-w-0 flex-1">조직 관리자 공유</span>
                                    <button
                                        type="button"
                                        className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded text-slate-500 hover:bg-slate-100 hover:text-slate-800"
                                        aria-label="조직 관리자 공유 설명"
                                        aria-expanded={busyShareHelpOpen}
                                        onClick={(e) => {
                                            e.preventDefault()
                                            setBusyShareHelpOpen((v) => !v)
                                        }}
                                    >
                                        <CircleHelp className="h-4 w-4" aria-hidden="true" />
                                    </button>
                                </label>
                                {busyShareHelpOpen && (
                                    <div className="absolute right-0 top-[calc(100%+0.25rem)] z-10 w-full rounded border border-slate-200 bg-white p-2 text-xs leading-snug text-slate-700 shadow-lg">
                                        소속 조직 및 상위 조직의 일정 관리자에게 제목·메모를 포함해 공유합니다.
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="hidden sm:block" aria-hidden />
                        )}
                    </div>
                    <div className="grid gap-2 sm:grid-cols-2">
                        <label className="min-w-0 text-sm">
                            <span className="text-slate-600">시작</span>
                            <input
                                type="datetime-local"
                                className="mt-1 w-full rounded border px-2 py-1.5 text-sm disabled:bg-slate-50 disabled:text-slate-500"
                                value={startLocal}
                                disabled={allDay}
                                onChange={(e) => setStartLocal(e.target.value)}
                            />
                        </label>
                        <label className="min-w-0 text-sm">
                            <span className="text-slate-600">종료</span>
                            <input
                                type="datetime-local"
                                className="mt-1 w-full rounded border px-2 py-1.5 text-sm disabled:bg-slate-50 disabled:text-slate-500"
                                value={endLocal}
                                disabled={allDay}
                                onChange={(e) => setEndLocal(e.target.value)}
                            />
                        </label>
                    </div>
                </div>
                {!startEndOrder.ok && (
                    <p className="text-xs text-rose-600" role="alert">
                        {startEndOrder.message}
                    </p>
                )}
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 text-sm">
                    <label className="block">
                        <span className="text-slate-600">반복 설정</span>
                        <select
                            className="mt-1 w-full rounded border px-2 py-1.5 text-sm bg-white disabled:bg-slate-50"
                            disabled={allDay}
                            value={rrule}
                            onChange={(e) => setRrule(e.target.value)}
                        >
                            {RRULE_PRESETS.map((p) => (
                                <option key={p.key} value={p.value}>{p.label}</option>
                            ))}
                        </select>
                        {allDay ? (
                            <p className="mt-0.5 text-[11px] text-slate-500">종일 일정은 반복 없음</p>
                        ) : null}
                    </label>
                    {(modal === 'personal' || modal === 'edit-personal') ? (
                        <label className="block">
                            <span className="text-slate-600">카테고리</span>
                            <select
                                className="mt-1 w-full rounded border px-2 py-1.5 text-sm bg-white"
                                value={personalCategory}
                                onChange={(e) => setPersonalCategory(e.target.value)}
                            >
                                {PERSONAL_CATEGORY_OPTIONS.map((o) => (
                                    <option key={o.value || 'none'} value={o.value}>{o.label}</option>
                                ))}
                            </select>
                        </label>
                    ) : (
                        <div className="flex min-h-[2.75rem] flex-col justify-end rounded border border-slate-100 bg-slate-50/80 px-2 py-1.5 text-xs text-slate-500">
                            조직 일정은 아래에서 대상 조직·인원을 지정합니다.
                        </div>
                    )}
                </div>
                {(modal === 'org' || modal === 'edit-org') && (
                    <div className="space-y-2">
                        <div className="text-sm text-slate-600">대상 조직 (조직별로 하위 포함 여부를 지정)</div>
                        <OrgMultiSelect
                            value={orgPickerIds}
                            onChange={syncOrgTargetsFromPicker}
                            scope="mine"
                        />
                        <div className="space-y-2">
                            {orgTargetConfig.map((row) => (
                                <div key={row.orgId} className="flex items-center justify-between gap-2 text-xs border rounded px-2 py-1">
                                    <span className="text-slate-700">{orgNameById.get(row.orgId) || `조직 #${row.orgId}`}</span>
                                    <label className="flex items-center gap-1 shrink-0">
                                        <input
                                            type="checkbox"
                                            checked={!!row.includeDescendants}
                                            onChange={(e) => {
                                                const v = e.target.checked
                                                setOrgTargetConfig((prev) => prev.map((x) => (x.orgId === row.orgId ? { ...x, includeDescendants: v } : x)))
                                            }}
                                        />
                                        하위 포함
                                    </label>
                                </div>
                            ))}
                        </div>
                        <div className="text-sm text-slate-600">지정 인원 (비우면 대상 조직 전원)</div>
                        <div className="max-h-36 overflow-y-auto border rounded p-2 text-xs space-y-1">
                            {candidates.length === 0 && <div className="text-slate-400">대상 조직을 먼저 선택하세요.</div>}
                            {candidates.map((u) => (
                                <label key={u.id} className="flex gap-2 items-center cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={explicitUserIds.includes(Number(u.id))}
                                        onChange={(e) => {
                                            const id = Number(u.id)
                                            setExplicitUserIds((prev) => {
                                                if (e.target.checked) return [...new Set([...prev, id])]
                                                return prev.filter((x) => x !== id)
                                            })
                                        }}
                                    />
                                    <span>{u.name}</span>
                                    <span className="text-slate-400 truncate">{u.email}</span>
                                </label>
                            ))}
                        </div>
                    </div>
                )}
                <fieldset className="rounded border border-slate-200 p-3">
                    <legend className="px-1 text-sm font-medium text-slate-700">알림</legend>
                    <p className="mb-2 text-xs text-slate-500">웹 알림과 앱 푸시 알림이 발송됩니다.</p>
                    <div className="grid gap-2 text-sm text-slate-700 sm:grid-cols-4">
                        <label className="flex items-center gap-2 rounded border border-slate-200 px-3 py-2">
                            <input
                                type="checkbox"
                                checked={!remindersEnabled}
                                onChange={(e) => {
                                    if (e.target.checked) clearReminders()
                                    else updateReminderChecked(60, true)
                                }}
                            />
                            <span>알림 없음</span>
                        </label>
                        <label className="flex items-center gap-2 rounded border border-slate-200 px-3 py-2">
                            <input
                                type="checkbox"
                                checked={rem10}
                                onChange={(e) => updateReminderChecked(10, e.target.checked)}
                            />
                            <span>10분 전</span>
                        </label>
                        <label className="flex items-center gap-2 rounded border border-slate-200 px-3 py-2">
                            <input
                                type="checkbox"
                                checked={rem60}
                                onChange={(e) => updateReminderChecked(60, e.target.checked)}
                            />
                            <span>1시간 전</span>
                        </label>
                        <label className="flex items-center gap-2 rounded border border-slate-200 px-3 py-2">
                            <input
                                type="checkbox"
                                checked={rem1440}
                                onChange={(e) => updateReminderChecked(1440, e.target.checked)}
                            />
                            <span>1일 전</span>
                        </label>
                    </div>
                </fieldset>
                <label className="block text-sm">
                    <span className="text-slate-600">설명·메모 (선택)</span>
                    <textarea className="mt-1 w-full rounded border px-3 py-2" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="참석자·장소·준비물 등" />
                </label>
                <div className="flex justify-end gap-2 pt-2">
                    <button type="button" className="rounded border px-3 py-1.5 text-sm" onClick={closeModal}>
                        닫기
                    </button>
                    {modal === 'personal' && (
                        <button
                            type="button"
                            className="rounded bg-sky-600 text-white px-3 py-1.5 text-sm disabled:opacity-50"
                            disabled={!title.trim() || !startEndOrder.ok || unresolvedCustomerSuggestion || createPersonalMut.isPending}
                            onClick={() => createPersonalMut.mutate()}
                        >
                            저장
                        </button>
                    )}
                    {modal === 'org' && (
                        <button
                            type="button"
                            className="rounded bg-sky-600 text-white px-3 py-1.5 text-sm disabled:opacity-50"
                            disabled={!title.trim() || !startEndOrder.ok || createOrgMut.isPending || !orgTargetConfig.length}
                            onClick={() => createOrgMut.mutate()}
                        >
                            저장
                        </button>
                    )}
                    {(modal === 'edit-personal' || modal === 'edit-org') && (
                        <button
                            type="button"
                            className="rounded bg-sky-600 text-white px-3 py-1.5 text-sm disabled:opacity-50"
                            disabled={!title.trim() || !startEndOrder.ok || unresolvedCustomerSuggestion || updateMut.isPending}
                            onClick={submitEdit}
                        >
                            수정 저장
                        </button>
                    )}
                </div>
            </div>
        </div>
    )
})

export default CalendarEventEditor
